/**
 * User-email hint store — last-known login email, used as a displayName
 * fallback during bootstrap when:
 *   1. /employees/me 404s (HR admins without an employee row, per be-030)
 *   2. JWT carries no `email`, `name`, or `preferred_username` claim
 *      (current backend ships only sub/aud/roles/tid — see fe-040)
 *
 * Without this hint, post-reload the avatar can only render initials from the
 * JWT `sub` (which is a UUID, useless). With this hint, we can derive
 * `email.split("@")[0]` → e.g. `"hr"` → initials `"HR"`.
 *
 * Security: same posture as `refreshTokenStore` (localStorage, same-origin
 * readable). Email is non-secret PII — XSS exposure is no worse than what an
 * attacker would harvest from the rendered DOM. Cleared on logout.
 *
 * Storage key kept distinct from `linkhr:refreshToken` so accidental clears
 * stay surgical.
 */
const STORAGE_KEY = "linkhr:userEmail";

let memoryFallback: string | null = null;

function tryLocalStorage<T>(op: (ls: Storage) => T, fallback: T): T {
  try {
    if (typeof window === "undefined") return fallback;
    const ls = window.localStorage;
    if (!ls) return fallback;
    return op(ls);
  } catch {
    return fallback;
  }
}

export const userEmailHintStore = {
  set(email: string): void {
    memoryFallback = email;
    tryLocalStorage((ls) => ls.setItem(STORAGE_KEY, email), undefined);
  },
  get(): string | null {
    const fromLs = tryLocalStorage((ls) => ls.getItem(STORAGE_KEY), null);
    return fromLs ?? memoryFallback;
  },
  clear(): void {
    memoryFallback = null;
    tryLocalStorage((ls) => ls.removeItem(STORAGE_KEY), undefined);
  },
};
